import React, { useState, useRef, useEffect } from "react"
import { twMerge } from "tailwind-merge"

// Helper function to get YouTube video ID from URL
const getYouTubeVideoID = (url) => {
  const regExp =
    /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/
  const match = url?.match(regExp)
  return match ? match[1] : null
}

const VideoThumbnail = ({
  videoUrl,
  className = "",
  showPlayIcon = false,
  onClick,
}) => {
  const [thumbnail, setThumbnail] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const videoRef = useRef(null)
  const containerRef = useRef(null)

  const isYouTube =
    videoUrl?.includes("youtube.com") || videoUrl?.includes("youtu.be")

  // Reset state when URL changes
  useEffect(() => {
    setThumbnail(null)
    setLoading(true)
    setError(null)

    // Clean up previous video if it exists
    const video = videoRef.current
    if (video) {
      video.removeAttribute("src")
      video.load()
    }
  }, [videoUrl])

  useEffect(() => {
    if (!videoUrl) {
      setError("No video URL provided")
      setLoading(false)
      return
    }

    // Handle YouTube videos
    if (isYouTube) {
      const videoId = getYouTubeVideoID(videoUrl)
      if (videoId) {
        // Use higher quality YouTube thumbnail
        const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
        setThumbnail(thumbnailUrl)
        setLoading(false)
      } else {
        setError("Invalid YouTube URL")
        setLoading(false)
      }
      return
    }

    // Handle other video types
    const video = videoRef.current
    if (!video) {
      setError("Video element not available")
      setLoading(false)
      return
    }

    let attempts = 0
    const maxAttempts = 5
    let thumbnailGenerated = false
    let seeked = false

    // Force the video to play for a short time to ensure frame loading
    const playVideoForFrame = async () => {
      if (thumbnailGenerated) return

      try {
        // Mute the video to allow autoplay
        video.muted = true
        video.volume = 0

        // Set playback rate to very slow to ensure frame loads properly
        video.playbackRate = 0.25

        // Try to play the video for a brief moment
        const playPromise = video.play()

        if (playPromise !== undefined) {
          await playPromise

          // Let it play for just a moment to ensure frame loads
          setTimeout(() => {
            video.pause()
            attemptCapture()
          }, 300)
        } else {
          attemptCapture()
        }
      } catch (err) {
        // If we can't play, try to capture anyway
        attemptCapture()
      }
    }

    const attemptCapture = () => {
      if (thumbnailGenerated) return

      try {
        // Check if video dimensions are available and reasonable
        if (video.videoWidth <= 0 || video.videoHeight <= 0) {
          attempts++
          if (attempts >= maxAttempts) {
            setThumbnail("/api/placeholder/320/240") // Use placeholder image
            setLoading(false)
            thumbnailGenerated = true
            removeListeners()
          } else {
            // Try again at a different position
            const newPosition =
              (attempts / maxAttempts) * (video.duration || 10)
            video.currentTime = newPosition
          }
          return
        }

        // Create canvas with video dimensions
        const canvas = document.createElement("canvas")
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight

        // Draw the current video frame
        const ctx = canvas.getContext("2d", { willReadFrequently: true })
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

        // Check if the canvas has actual content (not just white/transparent)
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const data = imageData.data

        // Check if image data is all white/transparent (empty)
        let hasContent = false
        for (let i = 0; i < data.length; i += 4) {
          // Check if pixel is not white/transparent
          // R, G, B values all < 250 (allowing for some compression artifacts)
          // or Alpha channel is not 0
          if (
            (data[i] < 250 || data[i + 1] < 250 || data[i + 2] < 250) &&
            data[i + 3] > 0
          ) {
            hasContent = true
            break
          }
        }

        if (!hasContent) {
          attempts++

          if (attempts >= maxAttempts) {
            // After max attempts, try using a placeholder image or fallback
            setThumbnail("/api/placeholder/320/240") // Use placeholder image
            setLoading(false)
            thumbnailGenerated = true
            removeListeners()
          } else {
            // Try a different position in the video
            const newPosition =
              (attempts / maxAttempts) * (video.duration || 10)
            video.currentTime = newPosition
            // Also try playing the video briefly
            setTimeout(playVideoForFrame, 100)
          }
          return
        }

        // If we have content, convert to data URL
        try {
          const dataUrl = canvas.toDataURL("image/jpeg", 0.8)

          thumbnailGenerated = true
          setThumbnail(dataUrl)
          setLoading(false)
          removeListeners()
        } catch (toDataUrlError) {
          // This might be a CORS issue
          setThumbnail("/api/placeholder/320/240")
          setLoading(false)
          thumbnailGenerated = true
          removeListeners()
        }
      } catch (err) {
        attempts++

        if (attempts >= maxAttempts) {
          setError("Failed to generate thumbnail")
          setLoading(false)
          removeListeners()
        } else {
          setTimeout(playVideoForFrame, 500)
        }
      }
    }

    // Handle when video can play
    const handleCanPlay = () => {
      if (!seeked && !thumbnailGenerated) {
        seeked = true
        video.currentTime = video.duration > 0 ? video.duration / 2 : 0
      }
    }

    const handleLoadedData = () => {
      if (!thumbnailGenerated) {
        // Try playing the video briefly to ensure frame loads
        playVideoForFrame()
      }
    }

    const handleLoadedMetadata = () => {
      if (!seeked && !thumbnailGenerated) {
        seeked = true
        video.currentTime = video.duration > 0 ? video.duration / 2 : 0
      }
    }

    const handleSeeked = () => {
      // After seeking, try playing briefly
      playVideoForFrame()
    }

    const handleError = (e) => {
      attempts++
      if (attempts < maxAttempts) {
        // Try different points in the video
        const seekPoint = (attempts / maxAttempts) * (video.duration || 10)
        video.currentTime = seekPoint
      } else {
        setError("Failed to generate thumbnail")
        setLoading(false)
        removeListeners()
      }
    }

    const handleTimeUpdate = () => {
      // Time update event (no action needed, just for tracking)
    }

    const removeListeners = () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata)
      video.removeEventListener("loadeddata", handleLoadedData)
      video.removeEventListener("canplay", handleCanPlay)
      video.removeEventListener("seeked", handleSeeked)
      video.removeEventListener("timeupdate", handleTimeUpdate)
      video.removeEventListener("error", handleError)

      // Make sure video is properly paused
      try {
        video.pause()
      } catch (e) {
        // Ignore pause errors
      }
    }

    // Set up event listeners
    video.addEventListener("loadedmetadata", handleLoadedMetadata)
    video.addEventListener("loadeddata", handleLoadedData)
    video.addEventListener("canplay", handleCanPlay)
    video.addEventListener("seeked", handleSeeked)
    video.addEventListener("timeupdate", handleTimeUpdate)
    video.addEventListener("error", handleError)

    // Reset video element
    video.muted = true
    video.preload = "auto"
    try {
      // Try with cors first (common issue with thumbnails)
      video.crossOrigin = "anonymous"
      video.src = videoUrl
      video.load()
    } catch (e) {
      // If that fails, try without cors
      video.removeAttribute("crossOrigin")
      video.src = videoUrl
      video.load()
    }

    // Set a timeout to prevent hanging indefinitely
    const timeoutId = setTimeout(() => {
      if (!thumbnailGenerated) {
        // Use placeholder if all else fails
        setThumbnail("/api/placeholder/320/240")
        setLoading(false)
        removeListeners()
      }
    }, 10000) // 10 seconds timeout

    // Return cleanup function
    return () => {
      clearTimeout(timeoutId)
      removeListeners()
    }
  }, [videoUrl, isYouTube])

  // Set up a global failsafe timeout
  useEffect(() => {
    // Create a global timeout for the whole process
    const failsafeTimeout = setTimeout(() => {
      if (loading && !thumbnail && !error) {
        setError("Thumbnail generation timed out")
        setLoading(false)
      }
    }, 15000) // 15 seconds global timeout

    return () => clearTimeout(failsafeTimeout)
  }, [loading, thumbnail, error])

  return (
    <div
      ref={containerRef}
      className={twMerge("relative overflow-hidden w-full h-full", className)}
      onClick={onClick}
    >
      {loading && (
        <div className="absolute inset-0 bg-admin_neutral_1/50 animate-pulse rounded" />
      )}

      {error && (
        <div className="absolute inset-0 bg-admin_neutral_1 flex items-center justify-center rounded">
          <span className="text-xs text-admin_text_secondary">{error}</span>
        </div>
      )}

      {thumbnail && (
        <div className="relative w-full h-full">
          <img
            src={thumbnail}
            alt="Video thumbnail"
            className="w-full h-full object-cover"
            onError={(e) => {
              setError("Error displaying thumbnail")
              setThumbnail(null)
            }}
          />
          {showPlayIcon && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 bg-black bg-opacity-60 rounded-full flex items-center justify-center">
                <div className="w-0 h-0 border-t-[8px] border-t-transparent border-l-[12px] border-l-white border-b-[8px] border-b-transparent ml-1" />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Always render the video element but keep it hidden */}
      <video
        ref={videoRef}
        className="hidden"
        preload="auto"
        muted
        playsInline
      />
    </div>
  )
}

export default VideoThumbnail
